import { differenceInDays, isPast, parseISO } from 'date-fns';

import { BaseTag } from '@/components/chariot-ui';
import { Status } from '@/sections/customerManagement/types';
import { CustomerTypeValue } from '@/types';
import { formatDate } from '@/utils/date.util';

interface LastCompromiseChipProps {
  lastCompromiseDate?: string;
  status: Status;
  type?: CustomerTypeValue;
  subscriptionEndDate?: string;
}

function getCompromiseChipColor(daysSinceCompromise: number): string {
  if (daysSinceCompromise <= 30) {
    return 'bg-chariot-status-info border-chariot-stroke-info text-white';
  }
  if (daysSinceCompromise <= 60) {
    return 'bg-chariot-status-medium border-chariot-stroke-medium text-white';
  }
  if (daysSinceCompromise <= 90) {
    return 'bg-chariot-status-high border-chariot-stroke-high text-white';
  }
  return 'bg-chariot-status-critical border-chariot-stroke-critical text-white';
}

const NONE_CHIP_COLOR =
  'bg-chariot-status-critical border-chariot-stroke-critical text-white';

function isPointInTimeType(type?: CustomerTypeValue): boolean {
  return type === 'PILOT' || type === 'ENGAGEMENT';
}

function getSubscriptionReferenceDate(subscriptionEndDate?: string): Date {
  if (subscriptionEndDate) {
    const endDate = parseISO(subscriptionEndDate);
    if (isPast(endDate)) return endDate;
  }
  return new Date();
}

export function LastCompromiseChip({
  lastCompromiseDate,
  status,
  type,
  subscriptionEndDate,
}: LastCompromiseChipProps) {
  // Point-in-time types (PILOT, ENGAGEMENT) use binary logic regardless of status
  if (isPointInTimeType(type)) {
    if (!lastCompromiseDate) {
      return (
        <BaseTag className={NONE_CHIP_COLOR} size="sm">
          Never
        </BaseTag>
      );
    }
    const formattedDate = formatDate(lastCompromiseDate, 'MMM d, yyyy', false);
    const infoChipColor =
      'bg-chariot-status-info border-chariot-stroke-info text-white';
    return (
      <BaseTag className={infoChipColor} size="sm">
        {formattedDate}
      </BaseTag>
    );
  }

  // Subscription types (MANAGED, SAAS, FREEMIUM, undefined) use time-degrading logic
  // Show chip for Active, Paused, Completed; plain text/dash for Setup, Upcoming
  const showsChip =
    status === 'Active' || status === 'Paused' || status === 'Completed';

  if (!lastCompromiseDate) {
    if (showsChip) {
      return (
        <BaseTag className={NONE_CHIP_COLOR} size="sm">
          Never
        </BaseTag>
      );
    }
    return <span className="text-default">-</span>;
  }

  const formattedDate = formatDate(lastCompromiseDate, 'MMM d, yyyy', false);

  if (!showsChip) {
    return <span className="text-default">{formattedDate}</span>;
  }

  const referenceDate = getSubscriptionReferenceDate(subscriptionEndDate);
  const daysSince = differenceInDays(
    referenceDate,
    parseISO(lastCompromiseDate)
  );
  const chipColor = getCompromiseChipColor(daysSince);

  return (
    <BaseTag className={chipColor} size="sm">
      {formattedDate}
    </BaseTag>
  );
}
